import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import Spinner from 'components/Spinner';

import usePortfolioMessages from 'hooks/usePortfolioMessages';

import './PortfolioMessages.css';

const PortfolioMessages = ({ portfolioId, portfolioUsers }) => {
  const {
    // State
    conversations,
    isLoadingConversations,
    conversationError,
    selectedConversationId,
    selectedConversation,
    messages,
    isLoadingMessages,
    messagesError,
    messageContent,
    isSending,
    showCreateForm,
    newConversationTitle,
    selectedParticipantIds,
    isCreatingConversation,
    createConversationError,
    mentionQuery,
    showMentionSuggestions,
    selectedMentionIndex,
    // mentionableUsers,
    availableParticipants,
    mentionSuggestions,
    currentUser,

    // Refs
    messagesEndRef,
    messageInputRef,
    mentionSuggestionsRef,

    // Functions
    setShowCreateForm,
    setNewConversationTitle,
    setSelectedParticipantIds,
    setCreateConversationError,

    setShowMentionSuggestions,
    setSelectedMentionIndex,
    handleConversationSelect,
    handleToggleParticipant,
    handleCreateConversation,
    handleMessageInputChange,
    handleMessageInputKeyDown,
    handleMentionSelect,
    handleSendMessage,
    renderMessageText,
    formatTextWithMentions,
    formatDateTime,
  } = usePortfolioMessages(portfolioId, portfolioUsers);

  // Ref for New Conversation dropdown to handle click-outside
  const createDropdownRef = useRef(null);

  // Close create conversation dropdown when clicking outside or pressing Escape
  useEffect(() => {
    if (!showCreateForm) return;

    const handleClickOutside = (event) => {
      if (createDropdownRef.current && !createDropdownRef.current.contains(event.target)) {
        setShowCreateForm(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowCreateForm(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showCreateForm, setShowCreateForm]);

  // Helper to extract clean initials for avatar
  const getConversationInitials = (title, participants) => {
    if (title && title.trim()) {
      const words = title.trim().split(/\s+/);
      if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase();
      }
      return title.trim().substring(0, 2).toUpperCase();
    }
    const otherParticipant = (participants || []).find((p) => p.id !== currentUser?.id);
    if (otherParticipant?.name) {
      const parts = otherParticipant.name.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return parts[0].substring(0, 2).toUpperCase();
    }
    if (otherParticipant?.email) {
      return otherParticipant.email.substring(0, 2).toUpperCase();
    }
    return '#';
  };

  // Helper function to get caret offset
  const getCaretOffset = (element, range) => {
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(element);
    preCaretRange.setEnd(range.endContainer, range.endOffset);
    return preCaretRange.toString().length;
  };

  // Helper function to set caret offset
  const setCaretOffset = (element, offset) => {
    const selection = window.getSelection();
    const range = document.createRange();
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      null
    );

    let node;
    let currentOffset = 0;

    while ((node = walker.nextNode()) !== null) {
      const nodeLength = node.textContent?.length || 0;
      if (currentOffset + nodeLength >= offset) {
        range.setStart(node, offset - currentOffset);
        range.setEnd(node, offset - currentOffset);
        selection.removeAllRanges();
        selection.addRange(range);
        return;
      }
      currentOffset += nodeLength;
    }

    // Fallback: set to end
    range.selectNodeContents(element);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  // Auto-resize input field (WhatsApp-like behavior)
  const autoResizeInput = (element) => {
    if (!element) return;

    // Reset height to get accurate scrollHeight
    element.style.height = 'auto';

    // Calculate the height based on content
    const scrollHeight = element.scrollHeight;
    const computedStyle = getComputedStyle(element);
    const lineHeight = parseFloat(computedStyle.lineHeight) || 24;
    const paddingTop = parseFloat(computedStyle.paddingTop) || 6;
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 6;
    const padding = paddingTop + paddingBottom;

    // Set min height (1 line) and max height (3 lines)
    const minHeight = lineHeight + padding;
    const maxHeight = lineHeight * 3 + padding;

    // Apply height within bounds
    const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);
    element.style.height = `${newHeight}px`;
  };

  // Sync contenteditable with formatted mentions
  useEffect(() => {
    if (!messageInputRef.current || messageContent === undefined) {
      return;
    }

    const element = messageInputRef.current;
    const currentText = element.textContent || '';
    const isFocused = document.activeElement === element;

    // Skip if text already matches (to avoid infinite loop)
    if (currentText === messageContent && isFocused) {
      // User is typing - format mentions in real-time
      const formatted = formatTextWithMentions(messageContent);
      if (element.innerHTML !== formatted && formatted) {
        const selection = window.getSelection();
        const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
        const caretOffset = range ? getCaretOffset(element, range) : messageContent.length;

        element.innerHTML = formatted;

        requestAnimationFrame(() => {
          setCaretOffset(element, caretOffset);
          autoResizeInput(element);
        });
      }
    } else if (currentText !== messageContent) {
      // Content changed externally - update HTML
      const formatted = formatTextWithMentions(messageContent);
      element.innerHTML = formatted || '';
      // Reset to default height if empty, otherwise auto-resize
      if (!messageContent || messageContent.trim() === '') {
        element.style.height = 'auto';
      } else {
        autoResizeInput(element);
      }
    }
  }, [messageContent, formatTextWithMentions, messageInputRef]);

  return (
    <div className="portfolio-detail-card portfolio-messages-card mb-4">
      <div className="portfolio-detail-card-header">
        <h2 className="portfolio-detail-card-title">Messages</h2>
      </div>
      <div className="portfolio-messages-wrapper">
        <div className="portfolio-conversation-list">
          <div className="portfolio-conversation-list-header">
            <div className="portfolio-conversation-header-title">
              <i className="bi bi-chat-dots me-2 text-dark" />
              <span>Conversations</span>
              {conversations.length > 0 && (
                <span className="portfolio-conversation-count-badge">
                  {conversations.length}
                </span>
              )}
            </div>

            <div className="position-relative" ref={createDropdownRef}>
              <button
                type="button"
                className="portfolio-messages-new-btn"
                onClick={() => setShowCreateForm((prev) => !prev)}
                aria-expanded={showCreateForm}
              >
                <i className="bi bi-plus-lg me-1" /> New
              </button>

              {showCreateForm && (
                <div className="portfolio-new-conversation-dropdown shadow-lg">
                  <div className="portfolio-new-conversation-dropdown-header">
                    <span>New Conversation</span>
                    <button
                      type="button"
                      className="portfolio-dropdown-close-btn"
                      onClick={() => {
                        setShowCreateForm(false);
                        setNewConversationTitle('');
                        setSelectedParticipantIds([]);
                        setCreateConversationError(null);
                      }}
                      aria-label="Close"
                    >
                      <i className="bi bi-x-lg" />
                    </button>
                  </div>

                  <form
                    className="portfolio-conversation-form"
                    onSubmit={handleCreateConversation}
                  >
                    <div className="mb-2">
                      <label className="form-label form-label-sm fw-semibold mb-1">
                        Title <span className="text-muted fw-normal">(optional)</span>
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={newConversationTitle}
                        onChange={(event) => setNewConversationTitle(event.target.value)}
                        maxLength={255}
                        placeholder="e.g. Unit 4B Updates"
                        autoFocus
                      />
                    </div>

                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <label className="form-label form-label-sm fw-semibold mb-0">
                          Participants <span className="text-danger">*</span>
                        </label>
                        {selectedParticipantIds.length > 0 && (
                          <span className="portfolio-participant-count-hint">
                            {selectedParticipantIds.length} selected
                          </span>
                        )}
                      </div>
                      <div className="portfolio-conversation-participants">
                        {availableParticipants.length === 0 ? (
                          <div className="text-muted small py-3 text-center">
                            No other team members available
                          </div>
                        ) : (
                          availableParticipants.map((user) => {
                            const isSelected = selectedParticipantIds.includes(Number(user.id));
                            const displayName = user.name || user.email || user.handle || 'User';

                            return (
                              <div
                                className={`portfolio-participant-checkbox-item ${
                                  isSelected ? 'selected' : ''
                                }`}
                                key={`participant-${user.id}`}
                                onClick={() => handleToggleParticipant(user.id)}
                                role="button"
                                tabIndex={0}
                                style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' }}
                                onKeyDown={(e) => {
                                  if (e.key === ' ' || e.key === 'Enter') {
                                    e.preventDefault();
                                    handleToggleParticipant(user.id);
                                  }
                                }}
                              >
                                <span className={`portfolio-custom-checkbox ${isSelected ? 'selected' : ''}`} aria-hidden="true">
                                  {isSelected && (
                                    <i className="bi bi-check-lg text-white" style={{ fontSize: '11px', lineHeight: 1 }} />
                                  )}
                                </span>
                                <div className="portfolio-participant-info" style={{ display: 'flex', flexDirection: 'column' }}>
                                  <span className="portfolio-participant-name" title={displayName}>
                                    {displayName}
                                  </span>
                                  {user.handle && (
                                    <span className="portfolio-participant-handle" title={`@${user.handle}`}>
                                      @{user.handle}
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>

                    {createConversationError && (
                      <div className="alert alert-danger py-1 px-2 small mb-2">
                        {createConversationError}
                      </div>
                    )}

                    <div className="d-flex justify-content-end gap-2 pt-2 border-top">
                      <button
                        type="button"
                        className="portfolio-messages-cancel-btn"
                        onClick={() => {
                          setShowCreateForm(false);
                          setNewConversationTitle('');
                          setSelectedParticipantIds([]);
                          setCreateConversationError(null);
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="portfolio-messages-submit-btn"
                        disabled={isCreatingConversation || selectedParticipantIds.length === 0}
                      >
                        {isCreatingConversation ? (
                          <>
                            <Spinner size="sm" className="me-1" /> Creating
                          </>
                        ) : (
                          'Create'
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>

          {isLoadingConversations ? (
            <div className="text-center py-4">
              <Spinner />
            </div>
          ) : conversationError ? (
            <div className="alert alert-danger m-3 py-2 small">
              Unable to load conversations.
            </div>
          ) : conversations.length === 0 ? (
            <div className="portfolio-conversation-empty text-center text-muted">
              <i className="bi bi-chat-square-dots mb-2 fs-4" />
              <p className="mb-0 fw-semibold">No conversations yet</p>
              <small>Click "New" to start one.</small>
            </div>
          ) : (
            <div className="portfolio-conversation-items-scroll">
              <ul className="portfolio-conversation-items">
                {conversations.map((conversation) => {
                  const isActive = conversation.id === selectedConversationId;
                  const participants = conversation.participants || [];
                  const participantNames = participants
                    .filter((participant) => participant.id !== currentUser?.id)
                    .map((participant) => participant.name || participant.email)
                    .join(', ');
                  const initials = getConversationInitials(conversation.title, participants);

                  return (
                    <li key={conversation.id} className="portfolio-conversation-item-wrapper">
                      <button
                        type="button"
                        className={`portfolio-conversation-item ${
                          isActive ? 'active' : ''
                        }`}
                        onClick={() => handleConversationSelect(conversation.id)}
                      >
                        <div className="portfolio-conversation-avatar">
                          {initials}
                        </div>
                        <div className="portfolio-conversation-item-body">
                          <div className="portfolio-conversation-item-header">
                            <div className="portfolio-conversation-item-title">
                              {conversation.title || participantNames || 'Conversation'}
                            </div>
                            <div className="portfolio-conversation-item-meta">
                              {formatDateTime(
                                conversation.last_message_at ||
                                conversation.created_at
                              )}
                            </div>
                          </div>
                          {conversation.last_message_preview && (
                            <div className="portfolio-conversation-item-preview">
                              {conversation.last_message_preview}
                            </div>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        <div className="portfolio-conversation-detail">
          {isLoadingMessages ? (
            <div className="text-center py-5">
              <Spinner />
            </div>
          ) : messagesError ? (
            <div className="alert alert-danger m-4">
              Unable to load messages.
            </div>
          ) : !selectedConversation ? (
            <div className="portfolio-conversation-empty-state">
              <div className="portfolio-conversation-empty-icon">
                <i className="bi bi-chat-left-dots" />
              </div>
              <h5>No Conversation Selected</h5>
              <p className="text-muted">
                Choose a conversation from the left to view messages, or click New to start one.
              </p>
            </div>
          ) : (
            <>
              <div className="portfolio-conversation-header">
                <div>
                  <h5 className="portfolio-conversation-header-title">
                    {selectedConversation.title || 'Conversation'}
                  </h5>
                </div>
                <div className="portfolio-conversation-participant-group">
                  {(selectedConversation.participants || []).map((participant) => (
                    <span key={`participant-chip-${participant.id}`} className="badge portfolio-participant-chip">
                      <i className="bi bi-person-circle me-1" />
                      {participant.name || participant.email}
                    </span>
                  ))}
                </div>
              </div>

              <div className="portfolio-message-feed">
                {messages.length === 0 ? (
                  <div className="portfolio-conversation-placeholder text-muted">
                    No messages yet. Be the first to say hello.
                  </div>
                ) : (
                  messages.map((message) => {
                    const senderName = message.sender?.name || message.sender?.email || 'User';
                    const isCurrentUser = message.sender?.id === currentUser?.id;

                    return (
                      <div
                        key={message.id}
                        className={`portfolio-message ${isCurrentUser ? 'portfolio-message--self' : 'portfolio-message--other'
                          }`}
                      >
                        <div className="portfolio-message-content">
                          {!isCurrentUser && (
                            <div className="portfolio-message-sender-name">
                              {senderName}
                            </div>
                          )}
                          <div className="portfolio-message-bubble">
                            <div className="portfolio-message-body">
                              {renderMessageText(message.message, message.mentions)}
                            </div>
                            <div className="portfolio-message-timestamp">
                              {formatDateTime(message.created_at)}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              <form className="portfolio-message-input" onSubmit={handleSendMessage}>
                <div className="portfolio-message-input-wrapper">
                  <div className="position-relative portfolio-message-input-container">
                    <div
                      ref={messageInputRef}
                      contentEditable
                      className="portfolio-message-input-editable form-control"
                      data-placeholder="Type a message... Use @ to mention teammates"
                      onInput={(e) => {
                        const text = e.target.textContent || e.target.innerText || '';
                        handleMessageInputChange({ target: { value: text } });
                        // Auto-resize input field
                        autoResizeInput(e.target);
                      }}
                      onKeyDown={handleMessageInputKeyDown}
                      onFocus={() => {
                        if (mentionQuery) {
                          setShowMentionSuggestions(true);
                        }
                      }}
                      onBlur={(e) => {
                        // Delay to allow click on suggestion to register
                        setTimeout(() => {
                          if (
                            !mentionSuggestionsRef.current?.contains(
                              document.activeElement
                            )
                          ) {
                            setShowMentionSuggestions(false);
                            setSelectedMentionIndex(-1);
                          }
                        }, 200);
                      }}
                      suppressContentEditableWarning
                    />

                    {showMentionSuggestions && mentionSuggestions.length > 0 && (
                      <div
                        ref={mentionSuggestionsRef}
                        className="portfolio-mention-suggestions"
                      >
                        {mentionSuggestions.map((user, index) => (
                          <button
                            type="button"
                            key={`mention-${user.id}`}
                            className={`portfolio-mention-suggestion ${index === selectedMentionIndex
                              ? 'portfolio-mention-suggestion--selected'
                              : ''
                              }`}
                            onClick={() => handleMentionSelect(user)}
                            onMouseEnter={() => setSelectedMentionIndex(index)}
                          >
                            <i className="bi bi-person-circle me-2" />
                            <strong>{user.name || user.email}</strong>
                            <span className="text-muted ms-1">@{user.handle}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="portfolio-message-send-button"
                    disabled={isSending || !messageContent.trim()}
                  >
                    {isSending ? (
                      <>
                        <Spinner size="sm" className="me-2" /> Sending...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-send me-2" /> Send
                      </>
                    )}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

PortfolioMessages.propTypes = {
  portfolioId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  portfolioUsers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
      email: PropTypes.string,
    })
  ),
};

PortfolioMessages.defaultProps = {
  portfolioUsers: [],
};

export default PortfolioMessages;
